import { InputChangeEvent } from '@progress/kendo-react-inputs';
import {
  TabStrip,
  TabStripTab,
  TabStripSelectEventArguments
} from '@progress/kendo-react-layout';
import { Row } from 'react-bootstrap';
import { useAuth } from 'react-oidc-context';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { appStore } from '../stores/app-store';
import Toolbar from '../components/page-toolbar';
import { StandardInput } from '../components/forms';
import { useToast } from '../contexts/toast-context';
import { PermissionType } from '../types/permission';
import UserPermission from '../components/permission';
import { ResllerInviteUserAsync } from '../services/users';
import { PostResellerUserRolesAsync } from '../services/roles';
import { PostEmailAvailabilityAsync } from '../services/user-email';
import { PutAppNotificationByUserIdAsync } from '../services/notification';
import { BreadcrumbLocation, PrivatePage } from '../components/private-page';
import { NotificationName } from '../types/responses/notification-type-response';

export default function InviteUser() {
  const { pathname } = useLocation();
  const isFacilitator = pathname.includes('facilitators');

  const auth = useAuth();
  const navigate = useNavigate();
  const onBackHandler = () => {
    navigate(isFacilitator ? '/facilitators' : '/users');
  };

  const breadcrumbs: BreadcrumbLocation[] = [
    { label: 'Dashboard', href: '/' },
    { label: 'Administration', href: '../administration' },
    { label: 'Users', href: '../administration/users' }
  ];

  const orgId = parseInt(auth.user?.profile.catalyst_org_id! as string);
  const [selected, setSelected] = React.useState<number>(0);
  const [emailError, setEmailError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [userPermission, setUserPermission] = useState<string[]>();
  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };
  const { onToastOpen } = useToast();

  const isEmailValid = (value: string) => {
    const emailRegex: RegExp = new RegExp(/\S+@\S+\.\S+/);

    return emailRegex.test(value) ? '' : 'Please enter a valid email.';
  };

  const emailChangeHandler = (e: InputChangeEvent) => {
    setEmail(e.target.value?.toString() ?? '');
  };

  const onPermissionChange = useCallback(
    (permission: string, value: boolean) => {
      // Define parent permissions
      const parentPermissions = [
        'GlobalLibrary',
        'MyLibrary',
        'Network',
        'Organisation',
        'OrgAdmin',
        'Events',
        'Reports',
        'Facilitator'
      ];

      if (value) {
        setUserPermission((prev) => {
          const userRoles = (auth.user?.profile.role as string[]) || [];
          let addedPermission = parentPermissions.includes(permission)
            ? userRoles.filter((role) => {
                return role === permission || role.startsWith(permission + '.');
              })
            : [permission];

          parentPermissions.forEach((parent) => {
            if (
              permission.startsWith(parent + '.') &&
              !prev?.includes(parent)
            ) {
              addedPermission.push(parent);
            }
          });

          return [
            ...(prev as PermissionType[]),
            ...(addedPermission as PermissionType[])
          ];
        });
      } else {
        setUserPermission((prev) => {
          if (!prev) return [];
          const newPermissions = new Set(prev);
          const isParent = !permission.includes('.');
          const parts = permission.split('.');

          if (isParent) {
            const permissionsToRemove = [...newPermissions].filter((p) =>
              p.startsWith(permission)
            );
            permissionsToRemove.forEach((p) => newPermissions.delete(p));
          } else {
            newPermissions.delete(permission);

            const parent = parts[0];
            const hasOtherChildren = [...newPermissions].some((p) =>
              p.startsWith(parent + '.')
            );

            if (!hasOtherChildren) {
              newPermissions.delete(parent);
            }
          }

          return Array.from(newPermissions);
        });
      }
    },
    [auth.user?.profile.role]
  );

  const onClickInviteHandler = async () => {
    try {
      appStore.showLoading();

      let isValidEmail = isEmailValid(email!);

      if (email === '') {
        let emailEmpty = 'Email is required.';
        onToastOpen({
          title: 'Email',
          body: <div>{emailEmpty}</div>,
          style: 'error'
        });

        setEmailError(emailEmpty);
        return;
      }

      if (isValidEmail !== '') {
        onToastOpen({
          title: 'Emnail',
          body: <div>{isValidEmail}</div>,
          style: 'error'
        });

        setEmailError(isValidEmail);
        return;
      }

      const available = await PostEmailAvailabilityAsync({
        emailAddress: email!
      });

      if (available) {
        const inAppNotification = [
          'Event.Created',
          'Event.Completed',
          'Game.Created',
          'Game.Published',
          'Game.Submitted',
          'Game.Approved',
          'Game.Duplicated',
          'Assessment.Created',
          'Assessment.Published',
          'Assessment.Submitted',
          'Assessment.Approved',
          'Assessment.Duplicated',
          'Feedback.Created',
          'Feedback.Published',
          'Feedback.Submitted',
          'Feedback.Approved',
          'Feedback.Duplicated',
          'GlobalLibrary.Game.Added',
          'GlobalLibrary.Assessment.Added',
          'GlobalLibrary.Feedback.Added'
        ];

        let response = await ResllerInviteUserAsync({
          firstName: '',
          lastName: '',
          emailAddress: { address: email },
          type: isFacilitator ? 'FacilitatorOnly' : 'Standard'
        });

        if (response.id !== undefined) {
          if (userPermission?.length === 0) {
            userPermission.push('Events');
          }

          // set all in-app notification settings on for new registered user
          await PutAppNotificationByUserIdAsync(
            response?.id,
            inAppNotification as NotificationName[]
          );

          await PostResellerUserRolesAsync(response?.id, userPermission!);
          setEmail('');
        }

        onToastOpen({
          title: `Invite ${isFacilitator ? 'facilitator' : 'user'}`,
          body: (
            <div>
              {isFacilitator ? 'Facilitator' : 'Member'} added.<br></br>
              <Link to={isFacilitator ? 'facilitators' : 'users'}>
                Go to {isFacilitator ? 'Facilitators' : 'Members'}.
              </Link>
            </div>
          ),
          style: 'success'
        });
      } else {
        onToastOpen({
          title: isFacilitator ? 'Facilitators' : 'Users',
          body: <div>{`Email already registered.`}</div>,
          style: 'error'
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      appStore.hideLoading();
    }
  };

  const defaultPermissions = () => {
    let userDefaultPermissions: string[] = [];
    (auth.user?.profile.role as PermissionType[]).forEach((f) => {
      // Based on https://dev.azure.com/catalyst-global/catalyst-games/_workitems/edit/6286
      // Have to change the default permission only for Organisation and Organisation.Info
      // Commented out the old code, just in case to revert it back in the future.
      // if (
      //   !f.includes('Admin') &&
      //   !f.includes('Members') &&
      //   !f.includes('DisplayLanguage')
      // )
      if (!isFacilitator && ['Organisation', 'Organisation.Info'].includes(f))
        userDefaultPermissions.push(f);

      if (
        isFacilitator &&
        ['Facilitator', 'Facilitator.Portal', 'Facilitator.Spark', 'Facilitator.Assessment'].includes(f)
      )
        userDefaultPermissions.push(f);
    });
    setUserPermission(userDefaultPermissions!);
  };

  const permissions = isFacilitator
    ? ['Facilitator', 'Facilitator.Portal', 'Facilitator.Spark', 'Facilitator.Assessment']
    : auth.user?.profile.role;

  useEffect(() => {
    defaultPermissions();
  }, []);

  return (
    <PrivatePage
      breadcrumb={`Dashboard/${isFacilitator ? 'Facilitators' : 'Users'}`}
      pageTitle={isFacilitator ? 'Facilitators' : 'Users'}>
      <Toolbar title={`New ${isFacilitator ? 'Facilitator' : 'User'}`}>
        <Button
          className={'me-1'}
          themeColor={'secondary'}
          onClick={() => onBackHandler()}>
          Back
        </Button>
        <Button
          className={'me-1'}
          themeColor={'primary'}
          onClick={onClickInviteHandler}>
          Send invite
        </Button>
      </Toolbar>
      <Row className={'mt-3'}>
        <TabStrip selected={selected} onSelect={handleSelect}>
          <TabStripTab title="Info">
            <div>
              <p>
                New {isFacilitator ? 'facilitator' : 'user'} will receive an
                email to complete their log in details.
              </p>
              <StandardInput
                label="Email"
                value={email ?? ''}
                onChange={emailChangeHandler}></StandardInput>
              <Error>{emailError}</Error>
            </div>
          </TabStripTab>
          <TabStripTab title="Permissions">
            <UserPermission
              permissions={permissions as PermissionType[]}
              userPermissions={userPermission as PermissionType[]}
              onChange={onPermissionChange}
              showAdminPermission={false}
              isFacilitator={isFacilitator}
              isAdmin={false}
            />
          </TabStripTab>
        </TabStrip>
      </Row>
    </PrivatePage>
  );
}
